import type { SettingsTemplateFormData } from '@/entities/settings-template'
import type {
	SettingsTemplateCreateModel,
	SettingsTemplateModel,
	SettingsTemplateUpdateModel,
} from '@/shared/generated'
import type { ResolverError, UseFormReturn } from 'react-hook-form'

import React, { createContext, useEffect } from 'react'
import { toNestErrors } from '@hookform/resolvers'
import { useQueryClient } from '@tanstack/react-query'
import { addDays, format } from 'date-fns'
import { any, prop } from 'lodash/fp'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { useSettingsSchemasQuery } from '@/entities/core'
import { formatLegacyTactics } from '@/entities/pricing-rule'
import {
	createSettingsTemplateSchemaAsync,
	useCreateSettingsTemplateMutation,
	useSettingsTemplateQuery,
	useUpdateSettingsTemplateMutation,
} from '@/entities/settings-template'

import { SettingsTemplateType } from '@/shared/generated'
import { ajvResolver } from '@/shared/lib/validator/ajv'
import { parseZod4Issues } from '@/shared/lib/validator/zod'
import { DateFormat } from '@/shared/tools/dates'
import {
	getResolvedSchema,
	getSchemaDefaultState,
} from '@/shared/tools/json-schema-utils'

export type SettingsTemplateContextType = {
	templateId?: string
	template_type: SettingsTemplateType
	isNew: boolean
	template?: SettingsTemplateModel
	isPending: boolean
	isLoading: boolean
	methods: UseFormReturn<SettingsTemplateFormData>
	handleFinish: () => void
	handleSubmit: () => void
	handleReset: () => void
}
/**
 * Stored bodies predate the rule-builder shape, and the tactics fields only read
 * the normalized one — without this an RB template opens with nothing selected.
 * Tactics-only templates carry `body.pricing_tactics`; the full pricing-campaign
 * template nests it under `body.settings`.
 */
const formatLegacyTemplateBody = (body: any) => {
	if (body?.settings?.pricing_tactics)
		return {
			...body,
			settings: {
				...body.settings,
				pricing_tactics: formatLegacyTactics(body.settings.pricing_tactics),
			},
		}

	if (body?.pricing_tactics)
		return {
			...body,
			pricing_tactics: formatLegacyTactics(body.pricing_tactics),
		}

	return body
}

const DEFAULT_PROMO_PRICING_TACTICS_PARAMS = {
	promo_start_date: format(new Date(), DateFormat.system),
	promo_end_date: format(addDays(new Date(), 6), DateFormat.system),
	target: {
		name: 'sales_items_uplift',
		value: 0,
	},
}

export default function SettingsTemplateProvider({
	children,
	id,
	template_type,
	onFinish,
}: {
	children: React.ReactNode
	id?: string
	template_type: SettingsTemplateType
	onFinish?: () => void
}) {
	const { t } = useTranslation()

	const queryClient = useQueryClient()
	const isNew = !id
	const template = useSettingsTemplateQuery(id!, {
		enabled: !!id,
	})
	const schemaQuery = useSettingsSchemasQuery({
		select: getResolvedSchema(`template:${template_type}`),
	})
	const createTemplateMutation = useCreateSettingsTemplateMutation({
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['settings-templates'],
			})
		},
	})
	const updateTemplateMutation = useUpdateSettingsTemplateMutation(id!, {
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['settings-templates'],
			})
		},
	})
	const methods = useForm<SettingsTemplateFormData>({
		async resolver(values, context, options) {
			const validateAjv = ajvResolver(schemaQuery.data!, {
				strict: false,
			})

			/**
			 * Use Zod for validation for smoke validation
			 */
			const { error, data } =
				await createSettingsTemplateSchemaAsync.safeParseAsync({
					...values,
					template_type,
				})
			if (error) {
				return {
					values: {},
					errors: toNestErrors(
						parseZod4Issues(
							error.issues,
							!options.shouldUseNativeValidation &&
								options.criteriaMode === 'all'
						),
						options
					),
				} satisfies ResolverError<SettingsTemplateFormData>
			}

			return validateAjv(data, context, options)
		},
	})

	useEffect(() => {
		if (template.data) {
			methods.reset({
				name: template.data?.name,
				body: formatLegacyTemplateBody(template.data?.body),
			})
		} else if (!template.data && schemaQuery.data) {
			methods.reset(getSchemaDefaultState(schemaQuery.data))
		}
	}, [template.data, schemaQuery.data, template_type])
	const handleReset = () => methods.reset()
	const handleSubmit = methods.handleSubmit(
		async (data: SettingsTemplateCreateModel | SettingsTemplateUpdateModel) => {
			if (isNew) {
				await createTemplateMutation.mutateAsync(
					{
						...data,
						template_type,
					} as SettingsTemplateCreateModel,
					{
						onError: () =>
							toast.error(
								t('toast.entity.failed', { entity: t('entity.template') }),
								{
									duration: 5000,
									id: 'settigs-template',
								}
							),
						onSuccess: () =>
							toast.success(
								t('toast.entity.created', { entity: t('entity.template') }),
								{
									duration: 5000,
									id: 'settigs-template',
								}
							),
					}
				)
				onFinish?.()
			} else {
				await updateTemplateMutation.mutateAsync(
					{
						...data,
					} as SettingsTemplateUpdateModel,
					{
						onError: () =>
							toast.error(
								t('toast.entity.failed', { entity: t('entity.template') }),
								{
									duration: 5000,
									id: 'settigs-template',
								}
							),
						onSuccess: () =>
							toast.success(
								t('toast.entity.updated', { entity: t('entity.template') }),
								{
									duration: 5000,
									id: 'settigs-template',
								}
							),
					}
				)
				onFinish?.()
			}
		}
	)
	const isPending = any(prop('isPending'), [
		createTemplateMutation,
		updateTemplateMutation,
	])
	const isLoading = any(prop('isLoading'), [template, schemaQuery])

	return (
		<SettingsTemplateContext
			value={{
				isNew,
				methods,
				isPending,
				isLoading,
				templateId: id,
				template: template?.data,
				template_type,
				handleFinish: () => onFinish?.(),
				handleReset,
				handleSubmit,
			}}
		>
			{children}
		</SettingsTemplateContext>
	)
}
export const SettingsTemplateContext =
	createContext<SettingsTemplateContextType>({
		isNew: true,
		isPending: false,
		isLoading: false,
		template_type: SettingsTemplateType.PricingCampaign,
		methods: {} as UseFormReturn<SettingsTemplateFormData>,
		handleFinish: () => {},
		handleSubmit: () => {},
		handleReset: () => {},
	})

export function useSettingsTemplateContext() {
	return React.use(SettingsTemplateContext)
}
